import React from "react";
import { useTranslation } from "react-i18next";
import { sum } from "lodash";

import { formatVolume } from "@utils/formatVolume";

/**
 * A monthly chart's figures as a table, for assistive tech: one row per month
 * and a closing period-total row, one column per series and a total column.
 * Volumes are in litres.
 */
const ChartDataTable: React.FunctionComponent<{
  /** The chart's title. */
  label: string;
  series: string[];
  dataset: Record<string, number | string>[];
}> = ({ label, series, dataset }) => {
  const { t } = useTranslation();
  const valuesOf = (row: Record<string, number | string>) =>
    series.map(name => Number(row[name] ?? 0));
  const totals = series.map(name =>
    sum(dataset.map(row => Number(row[name] ?? 0))),
  );

  // The div hides the table: a table ignores sr-only's width and overflow, so its rows would widen the page.
  return (
    <div className="sr-only">
      <table aria-label={t("zenith.ui.chart_data", { chart: label }) as string}>
        <thead>
          <tr>
            <th scope="col">{t("zenith.ui.month")}</th>
            {series.map(name => (
              <th key={name} scope="col">
                {name}
              </th>
            ))}
            <th scope="col">{t("common.total")}</th>
          </tr>
        </thead>
        <tbody>
          {dataset.map(row => (
            <tr key={row.month}>
              <th scope="row">{row.month}</th>
              {valuesOf(row).map((value, i) => (
                <td key={series[i]}>{formatVolume(value)}</td>
              ))}
              <td>{formatVolume(sum(valuesOf(row)))}</td>
            </tr>
          ))}
          <tr>
            <th scope="row">{t("zenith.ui.period_total")}</th>
            {totals.map((value, i) => (
              <td key={series[i]}>{formatVolume(value)}</td>
            ))}
            <td>{formatVolume(sum(totals))}</td>
          </tr>
        </tbody>
      </table>
    </div>
  );
};

export default ChartDataTable;
